// hover template open with text animation

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Header.scss';
import logo from '../images/Nora img updated.png';

interface MenuItemData {
  label: string;
  path: string;
}

interface MenuSection {
  title: string;
  items: MenuItemData[];
}

interface MenuData {
  title: string;
  sections: MenuSection[];
}

interface MenuItem {
  icon: string;
  label: string;
  isDirectLink?: boolean;
  link?: string;
}

type MenuKey = 'home' | 'whereWeWork' | 'whatWeDo' | 'partners' | 'about' | 'jobs' | 'contact' | 'projects';

const Header = () => {
  const [activeTab, setActiveTab] = useState<MenuKey | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<MenuKey | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Scroll function for Projects
  const scrollToProjects = () => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const projectsSection = document.getElementById('projects-section');
        if (projectsSection) {
          projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    } else {
      const projectsSection = document.getElementById('projects-section');
      if (projectsSection) {
        projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const menuData: Record<MenuKey, MenuData> = {
    home: { title: 'Home', sections: [{ title: 'Welcome', items: [] }] },
    about: { title: 'About Our Organization', sections: [{ title: 'Our Organization', items: [] }] },
    whatWeDo: {
      title: 'Our Programs & Activities',
      sections: [{ 
        title: 'What We Do', 
        items: [
          { label: 'Enhance Health Services', path: '/health' },
          { label: 'Quality Education', path: '/education' },
          { label: 'Empower Women & Children', path: '/women' },
          { label: 'AI & Digital Skills', path: '/freelancing' },
        ] 
      }]
    },
    whereWeWork: {
      title: 'Where We Work',
      sections: [
        { 
          title: 'Our Presence', 
          items: [
            { label: 'National Coverage', path: '/work/national-coverage' },
            { label: 'Regional Offices', path: '/work/regional-offices' },
            { label: 'Field Locations', path: '/work/field-locations' }
          ] 
        },
        { 
          title: 'Operational Areas', 
          items: [
            { label: 'Northern Region', path: '/work/regions/northern' },
            { label: 'Eastern Region', path: '/work/regions/eastern' },
            { label: 'Western Region', path: '/work/regions/western' },
            { label: 'Southern Region', path: '/work/regions/southern' }
          ] 
        }
      ]
    },
    partners: { 
      title: 'Our Partners & Supporters', 
      sections: [{ title: 'Collaboration', items: [{ label: 'Donors', path: '/donors' }, { label: 'Partners', path: '/partners' }] }] 
    },
    jobs: {
      title: 'Career Opportunities',
      sections: [{ title: 'Jobs', items: [{ label: 'Job Center', path: '/ComingSoon' }, { label: 'Application Form', path: '/JobsForm' }] }]
    },
    contact: { title: 'Contact Us', sections: [{ title: 'Get In Touch', items: [] }] },
    // ADD PROJECTS TO menuData (empty sections since it's a direct link)
    projects: { title: 'Projects', sections: [{ title: 'Projects', items: [] }] }
  };

   const menuItems: Record<MenuKey, MenuItem> = {
    home: { icon: 'fas fa-home', label: 'Home' },
    about: { icon: 'fas fa-users', label: 'Who We Are', isDirectLink: true, link: '/vision' },
    whatWeDo: { icon: 'fas fa-seedling', label: 'What We Do' },
    whereWeWork: { icon: 'fas fa-map-marker-alt', label: 'Where We Work', isDirectLink: true, link: '/where-we-work' },
    projects: { icon: 'fas fa-project-diagram', label: 'Projects', isDirectLink: true, link: '/' },
    partners: { icon: 'fas fa-handshake', label: 'Partners' },
    jobs: { icon: 'fas fa-briefcase', label: 'Jobs', isDirectLink: true, link: '/jobs' }, // ← CHANGE THIS to '/jobs'
    contact: { icon: 'fas fa-envelope', label: 'Contact', isDirectLink: true, link: '/contact' }
  };

  // --- Hover Handlers ---
  const handleMouseEnter = (key: MenuKey) => {
    if (window.innerWidth > 768) {
      const item = menuItems[key];
      if (!item.isDirectLink && key !== 'home' && key !== 'contact') {
        setActiveTab(key);
      } else {
        setActiveTab(null);
      }
    }
  };

  const handleHeaderMouseLeave = () => {
    if (window.innerWidth > 768) {
      setActiveTab(null);
    }
  };

  const handleTabClick = (menuKey: MenuKey) => {
    if (menuKey === 'home') navigate('/');
    else if (menuKey === 'contact') navigate('/contact');
    else if (menuKey === 'projects') {
      scrollToProjects();
      setActiveTab(null);
    }
    else {
      const menuItem = menuItems[menuKey];
      if (menuItem.isDirectLink && menuItem.link) {
        navigate(menuItem.link);
        setActiveTab(null);
      }
    }
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setMobileActiveTab(null);
  };

  const handleMobileMainItemClick = (menuKey: MenuKey) => {
    const item = menuItems[menuKey];
    if (menuKey === 'home' || menuKey === 'contact' || (item.isDirectLink && item.link)) {
      if (menuKey === 'projects') {
        scrollToProjects();
        setIsMobileMenuOpen(false);
      } else {
        navigate(item.link || '/');
        setIsMobileMenuOpen(false);
      }
    } else {
      setMobileActiveTab(mobileActiveTab === menuKey ? null : menuKey);
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`header-component ${isSticky ? 'header-sticky' : ''}`} ref={headerRef} onMouseLeave={handleHeaderMouseLeave}>
      <header className="header-main">
        <div className="header-container">
          <div className="header-top">
            <div className="header-logo">
              <Link to="/"><img src={logo} alt="NORA Logo" /></Link>
            </div>

            <div className="header-organization-name">
              <div className="organization-title-main">
                Noor Organization for Rehabilitation of Afghanistan
              </div>
              <div className="organization-subtitle">
                
              </div>
            </div>

            <div className="header-donate">
              <Link to="/donate" className="donate-btn">
                <i className="fas fa-heart"></i> Donate Now
              </Link>
            </div>
            <button className="mobile-menu-toggle" onClick={handleMobileMenuToggle}>
              <i className="fas fa-bars"></i>
            </button>
          </div>
        </div>
      </header>

      <nav className="header-nav desktop-nav">
        <div className="header-container">
          <ul className="nav-tabs-list">
            {(Object.entries(menuItems) as [MenuKey, MenuItem][]).map(([key, { icon, label, isDirectLink }]) => (
              <li key={key} className="nav-tab-item" onMouseEnter={() => handleMouseEnter(key)}>
                <button
                  className={`nav-tab-btn ${activeTab === key ? 'nav-tab-active' : ''} ${isDirectLink ? 'direct-link' : ''}`}
                  onClick={() => handleTabClick(key)}
                >
                  <i className={icon}></i> <span>{label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile Nav Overlay */}
      <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'mobile-nav-open' : ''}`} ref={mobileMenuRef}>
        <div className="mobile-nav-header">
          <button className="mobile-menu-close" onClick={handleMobileMenuToggle}><i className="fas fa-times"></i></button>
        </div>
        {!mobileActiveTab ? (
          <nav className="mobile-nav">
            <ul className="mobile-nav-list">
              {(Object.entries(menuItems) as [MenuKey, MenuItem][]).map(([key, { icon, label, isDirectLink }]) => (
                <li key={key} className="mobile-nav-item">
                  <button className="mobile-nav-btn" onClick={() => handleMobileMainItemClick(key)}>
                    <i className={icon}></i> <span>{label}</span>
                    <i className={`fas ${isDirectLink ? 'fa-external-link-alt' : 'fa-chevron-right'} mobile-nav-arrow`}></i>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ) : (
          <div className="mobile-dropdown-panel">
            <div className="mobile-dropdown-header">
              <button className="mobile-dropdown-back" onClick={() => setMobileActiveTab(null)}><i className="fas fa-chevron-left"></i> Back</button>
              <h3 className="mobile-dropdown-title">{menuData[mobileActiveTab]?.title}</h3>
            </div>
            <div className="mobile-dropdown-content">
              {menuData[mobileActiveTab]?.sections?.map((section, idx) => (
                <div key={idx} className="mobile-dropdown-section">
                  <h4 className="mobile-dropdown-section-title">{section.title}</h4>
                  <ul className="mobile-dropdown-list">
                    {section.items.map((item, iidx) => (
                      <li key={iidx} className="mobile-dropdown-item">
                        <button className="mobile-dropdown-item-btn" onClick={() => { navigate(item.path); setIsMobileMenuOpen(false); }}>
                          <span>{item.label}</span><i className="fas fa-chevron-right"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Tab Panel */}
      {activeTab && activeTab !== 'home' && !menuItems[activeTab]?.isDirectLink && (
        <div className="tab-panel-wrapper" ref={dropdownRef} onMouseEnter={() => setActiveTab(activeTab)}>
          <div className="header-container">
            <div className="tab-panel-content">
              <h2 className="tab-panel-title" style={{color: '#0aef2c', fontSize: '30px'}}>{menuData[activeTab]?.title}</h2>
              <div className="tab-sections-grid">
                {menuData[activeTab]?.sections?.map((section, idx) => (
                  <div key={idx} className="tab-section-block">
                    <h3 className="section-title">{section.title}</h3>
                    <ul className="section-list">
                      {section.items.map((item, iidx) => (
                        <li key={iidx}><a href="#" onClick={(e) => { e.preventDefault(); navigate(item.path); setActiveTab(null); }}>{item.label}</a></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;










































// Prevoius template import { useState, useRef, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import './Header.scss';
// import logo from '../images/Nora img updated.png';

// interface MenuItemData {
//   label: string;
//   path: string;
// }

// interface MenuSection {
//   title: string;
//   items: MenuItemData[];
// }

// interface MenuData {
//   title: string;
//   sections: MenuSection[];
// }

// interface MenuItem {
//   icon: string;
//   label: string;
//   isDirectLink?: boolean;
//   link?: string;
// }

// type MenuKey = 'home' | 'whereWeWork' | 'whatWeDo' | 'performance' | 'partners' | 'about' | 'jobs' | 'contact';

// const Header = () => {
//   const [activeTab, setActiveTab] = useState<MenuKey | null>(null);
//   const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
//   const [isSticky, setIsSticky] = useState(false);
//   const [mobileActiveTab, setMobileActiveTab] = useState<MenuKey | null>(null);
//   const dropdownRef = useRef<HTMLDivElement>(null);
//   const mobileMenuRef = useRef<HTMLDivElement>(null);
//   const headerRef = useRef<HTMLDivElement>(null);
//   const navigate = useNavigate();

//   // Menu data
//   const menuData: Record<MenuKey, MenuData> = {
//     home: { 
//       title: 'Home', 
//       sections: [{ 
//         title: 'Welcome', 
//         items: [] 
//       }] 
//     },
//     about: {
//       title: 'About Our Organization',
//       sections: [
//         { 
//           title: 'Our Organization', 
//           items: [] 
//         },
//       ]
//     },
//     whatWeDo: {
//       title: 'Our Programs & Activities',
//       sections: [
//         { 
//           title: 'Where We Focus', 
//           items: [
//             { label: 'Health', path: '/health' },
//             { label: 'Education', path: '/education' },
//             { label: 'Women Empowerment', path: '/women' },
//             { label: 'Freelancing', path: '/freelancing' },
//           ] 
//         },
//       ]
//     },
//     performance: {
//       title: 'Our Projects & Performance',
//       sections: [
//         { 
//           title: 'Ongoing Projects', 
//           items: [
//             { label: 'Empower Afghan Future', path: '/projects/ongoing/climate-resilience' },
//             { label: 'Reward Project', path: '/projects/ongoing/community-development' }
//           ] 
//         },
//       ]
//     },
//     whereWeWork: {
//       title: 'Where We Work',
//       sections: [
//         { 
//           title: 'Our Presence', 
//           items: [
//             { label: 'National Coverage', path: '/work/national-coverage' },
//             { label: 'Regional Offices', path: '/work/regional-offices' },
//             { label: 'Field Locations', path: '/work/field-locations' }
//           ] 
//         },
//         { 
//           title: 'Operational Areas', 
//           items: [
//             { label: 'Northern Region', path: '/work/regions/northern' },
//             { label: 'Eastern Region', path: '/work/regions/eastern' },
//             { label: 'Western Region', path: '/work/regions/western' },
//             { label: 'Southern Region', path: '/work/regions/southern' }
//           ] 
//         },
//         { 
//           title: 'Impact Mapping', 
//           items: [
//             { label: 'Community Reach', path: '/work/impact/community-reach' },
//             { label: 'Project Locations', path: '/work/impact/project-locations' },
//             { label: 'Success Stories', path: '/work/impact/success-stories' }
//           ] 
//         }
//       ]
//     },
//     partners: { 
//       title: 'Our Partners & Supporters', 
//       sections: [
//         { 
//           title: 'Collaboration', 
//           items: [
//             { label: 'Donors', path: '/donors' },
//             { label: 'Partners', path: '/partners' },
//           ] 
//         }
//       ] 
//     },
//     jobs: {
//       title: 'Career Opportunities',
//       sections: [
//         { 
//           title: 'Jobs', 
//           items: [
//             { label: 'Job Center', path: '/Jobs' },
//             { label: 'Application Form', path: '/JobsForm' }
//           ] 
//         },
//       ]
//     },
//     contact: { 
//       title: 'Contact Us', 
//       sections: [{ 
//         title: 'Get In Touch', 
//         items: [] 
//       }] 
//     }
//   };

//   const menuItems: Record<MenuKey, MenuItem> = {
//     home: { icon: 'fas fa-home', label: 'Home' },
//     whatWeDo: { icon: 'fas fa-hands-helping', label: 'What We Do' },
//     whereWeWork: { 
//       icon: 'fas fa-map-marker-alt', 
//       label: 'Where We Work',
//       isDirectLink: true,
//       link: '/where-we-work'
//     },
//     performance: { icon: 'fas fa-chart-line', label: 'Performance' },
//     partners: { icon: 'fas fa-handshake', label: 'Partners' },
//     jobs: { icon: 'fas fa-briefcase', label: 'Jobs', isDirectLink:true, link:'/Jobs' },
//     about: { icon: 'fas fa-info-circle', label: 'About', isDirectLink:true, link:'/vision' },
//     contact: { 
//       icon: 'fas fa-envelope', 
//       label: 'Contact',
//       isDirectLink: true,
//       link: '/contact'
//     }
//   };


//   // Handle desktop tab click                       
//   const handleTabClick = (menuKey: MenuKey) => {
//     if (menuKey === 'home') {
//       navigate('/');
//       return;
//     }
    
//     if (menuKey === 'contact') {
//       navigate('/contact');
//       return;
//     }

//     const menuItem = menuItems[menuKey];
//     if (menuItem.isDirectLink && menuItem.link) {
//       navigate(menuItem.link);
//       setActiveTab(null);
//       return;
//     }
    
//     if (activeTab === menuKey) {
//       setActiveTab(null);
//     } else {
//       setActiveTab(menuKey);
//     }
//   };

//   // Handle mobile menu toggle
//   const handleMobileMenuToggle = () => {
//     setIsMobileMenuOpen(!isMobileMenuOpen);
//     setMobileActiveTab(null);
//   };

//   // Handle mobile main menu item click
//   const handleMobileMainItemClick = (menuKey: MenuKey) => {
//     if (menuKey === 'home') {
//       navigate('/');
//       setIsMobileMenuOpen(false);
//       return;
//     }

//     if (menuKey === 'contact') {
//       navigate('/contact');
//       setIsMobileMenuOpen(false);
//       return;
//     }

//     const menuItem = menuItems[menuKey];
//     if (menuItem.isDirectLink && menuItem.link) {
//       navigate(menuItem.link);
//       setIsMobileMenuOpen(false);
//       return;
//     }

//     // For dropdown menus, show the submenu
//     if (mobileActiveTab === menuKey) {
//       setMobileActiveTab(null);
//     } else {
//       setMobileActiveTab(menuKey);
//     }
//   };

//   // Handle mobile dropdown item click
//   const handleMobileDropdownItemClick = (path: string) => {
//     navigate(path);
//     setIsMobileMenuOpen(false);
//     setMobileActiveTab(null);
//   };

//   // Handle navigation from desktop dropdown items
//   const handleDropdownItemClick = (path: string) => {
//     navigate(path);
//     setActiveTab(null);
//   };

//   // Handle mobile back button
//   const handleMobileBack = () => {
//     setMobileActiveTab(null);
//   };

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event: MouseEvent) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
//         setActiveTab(null);
//       }
//       if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) &&
//           !(event.target as Element).closest('.mobile-menu-toggle')) {
//         setIsMobileMenuOpen(false);
//         setMobileActiveTab(null);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   // Sticky header on scroll
//   useEffect(() => {
//     const handleScroll = () => setIsSticky(window.scrollY > 100);
//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, []);

//   return (
//     <div className={`header-component ${isSticky ? 'header-sticky' : ''}`} ref={headerRef}>
//       <header className="header-main">
//         <div className="header-container">
//           <div className="header-top">
//             <div className="header-logo">
//               <a href="/"><img src={logo} alt="NORA Logo" /></a>
//             </div>

//             <div className="header-tagline">
//               <i className="far fa-arrow-alt-circle-down"></i> <span className='header-top-bar'> <span >Gateway to</span> <span>prosperity, health,</span> <span>and education</span>  </span> 
//             </div>
//             <div className="header-donate">
//               <a href="/donate" className="donate-btn">
//                 <i className="fas fa-heart"></i> Donate Now
//               </a>
//             </div>
//             <button className="mobile-menu-toggle" onClick={handleMobileMenuToggle}>
//               <i className="fas fa-bars"></i>
//             </button>
//           </div>
//         </div>
//       </header>

//       {/* Desktop Navigation */}
//       <nav className="header-nav desktop-nav">
//         <div className="header-container">
//           <ul className="nav-tabs-list">
//             {(Object.entries(menuItems) as [MenuKey, MenuItem][]).map(([key, { icon, label, isDirectLink }]) => (
//               <li key={key} className="nav-tab-item">
//                 <button
//                   className={`nav-tab-btn ${activeTab === key ? 'nav-tab-active' : ''} ${isDirectLink ? 'direct-link' : ''}`}
//                   onClick={() => handleTabClick(key)}
//                 >
//                   <i className={icon}></i> <span>{label}</span>
//                   {activeTab === key && !isDirectLink && (
//                     <span style={{color: 'green', fontSize: '8px', position: 'absolute', top: '5px', right: '5px'}}>●</span>
//                   )}
//                 </button>
//               </li>
//             ))}
//           </ul>
//         </div>
//       </nav>

//       {/* Mobile Navigation */}
//       <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'mobile-nav-open' : ''}`} ref={mobileMenuRef}>
//         <div className="mobile-nav-header">
//           <button className="mobile-menu-close" onClick={handleMobileMenuToggle}>
//             <i className="fas fa-times"></i>
//           </button>
//         </div>
        
//         {/* Mobile Main Menu (shown when no dropdown is active) */}
//         {!mobileActiveTab && (
//           <nav className="mobile-nav">
//             <ul className="mobile-nav-list">
//               {(Object.entries(menuItems) as [MenuKey, MenuItem][]).map(([key, { icon, label, isDirectLink }]) => (
//                 <li key={key} className="mobile-nav-item">
//                   <button
//                     className={`mobile-nav-btn ${mobileActiveTab === key ? 'mobile-nav-active' : ''} ${isDirectLink ? 'direct-link' : ''}`}
//                     onClick={() => handleMobileMainItemClick(key)}
//                   >
//                     <i className={icon}></i> <span>{label}</span>
//                     {isDirectLink && <i className="fas fa-external-link-alt mobile-nav-arrow"></i>}
//                     {!isDirectLink && <i className="fas fa-chevron-right mobile-nav-arrow"></i>}
//                   </button>
//                 </li>
//               ))}
//             </ul>
//           </nav>
//         )}

//         {/* Mobile Dropdown Menu (shown when a dropdown is active) */}
//         {mobileActiveTab && mobileActiveTab !== 'home' && !menuItems[mobileActiveTab].isDirectLink && (
//           <div className="mobile-dropdown-panel">
//             <div className="mobile-dropdown-header">
//               <button className="mobile-dropdown-back" onClick={handleMobileBack}>
//                 <i className="fas fa-chevron-left"></i> Back
//               </button>
//               <h3 className="mobile-dropdown-title">{menuData[mobileActiveTab].title}</h3>
//             </div>
//             <div className="mobile-dropdown-content">
//               {menuData[mobileActiveTab].sections.map((section, idx) => (
//                 <div key={idx} className="mobile-dropdown-section">
//                   <h4 className="mobile-dropdown-section-title">{section.title}</h4>
//                   <ul className="mobile-dropdown-list">
//                     {section.items.map((item, iidx) => (
//                       <li key={iidx} className="mobile-dropdown-item">
//                         <button
//                           className="mobile-dropdown-item-btn"
//                           onClick={() => handleMobileDropdownItemClick(item.path)}
//                         >
//                           <span>{item.label}</span>
//                           <i className="fas fa-chevron-right"></i>
//                         </button>
//                       </li>
//                     ))}
//                   </ul>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Desktop Tab Panel */}
//       {activeTab && activeTab !== 'home' && !menuItems[activeTab].isDirectLink && (
//         <div className="tab-panel-wrapper" ref={dropdownRef}>
//           <div className="header-container">
//             <button className="tab-panel-close" onClick={() => setActiveTab(null)}>
//               <i className="fas fa-times"></i> 
//             </button>
//             <div className="tab-panel-content">
//               <h2 className="tab-panel-title" style={{color: 'green', fontSize: '30px'}}>
//                 {menuData[activeTab].title}
//               </h2>
//               <div className="tab-sections-grid">
//                 {menuData[activeTab].sections.map((section, idx) => (
//                   <div key={idx} className="tab-section-block">
//                     <h3 className="section-title">{section.title}</h3>
//                     <ul className="section-list">
//                       {section.items.map((item, iidx) => (
//                         <li key={iidx}>
//                           <a 
//                             href="#" 
//                             onClick={(e) => {
//                               e.preventDefault();
//                               handleDropdownItemClick(item.path);
//                             }}
//                           >
//                             {item.label}
//                           </a>
//                         </li>
//                       ))}
//                     </ul>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;